'use client';

import { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import AppImage from '@/components/Image';
import UserAvatar from '@/components/UserAvatar';
import { ParentSearchResultsPanel } from '@/components/parent/ParentSearchResultsPanel';
import { PublicStudyAuthPromptModal, type PublicStudyAuthPromptReason } from '@/components/public-study/PublicStudyAuthPrompt';
import type { Quest } from '@/constant/schema';
import { isParentProfile } from '@/lib/profile-role';
import type { TextbookTocData } from '@/lib/textbook-toc';
import useAuthStore from '@/store/useAuthStore';
import type { ParentSearchItem, ParentSearchResponse } from '@/types/parent-search';

type SearchPageClientProps = {
  initialQuery: string;
  initialSubject: string;
  initialGrade: string;
  initialData?: ParentSearchResponse | null;
  initialPreviewQuest?: Quest | null;
  initialPreviewToc?: TextbookTocData | null;
  initialDisableCorrection?: boolean;
  requireAuthForInternalActions?: boolean;
  searchNavigationPath?: string;
};

export default function SearchPageClient({
  initialQuery,
  initialSubject,
  initialGrade,
  initialData = null,
  initialPreviewQuest = null,
  initialPreviewToc = null,
  initialDisableCorrection = false,
  requireAuthForInternalActions = false,
  searchNavigationPath,
}: SearchPageClientProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const user = useAuthStore((state) => state.user);
  const profile = useAuthStore((state) => state.profile);
  const [query, setQuery] = useState(initialQuery);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const [authPromptReason, setAuthPromptReason] = useState<PublicStudyAuthPromptReason>('complete-question');
  const disableCorrectionFromUrl = searchParams.get('disableCorrection') === '1';
  const navigationPath = searchNavigationPath || pathname;
  const canAssignActions = !user || isParentProfile(profile);

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  const current = searchParams.toString();
  const redirectTo = current ? `${pathname}?${current}` : pathname;

  const openSignIn = () => {
    router.push(`/?show_login=1&redirect=${encodeURIComponent(redirectTo)}`);
  };

  const requestAuthPrompt = (reason: PublicStudyAuthPromptReason = 'complete-question') => {
    setAuthPromptReason(reason);
    setAuthPromptOpen(true);
  };

  const handleSearchStateChange = (next: { query: string; subject: string; grade: string; disableCorrection?: boolean }) => {
    setQuery(next.query);

    const currentQuery = searchParams.get('q') || '';
    const currentSubject = searchParams.get('subject') || 'all';
    const currentGrade = searchParams.get('grade') || 'all';
    const currentDisableCorrection = searchParams.get('disableCorrection') === '1';

    const nextParams = new URLSearchParams(searchParams.toString());
    if (next.query) {
      nextParams.set('q', next.query);
    } else {
      nextParams.delete('q');
    }

    if (next.subject && next.subject !== 'all') {
      nextParams.set('subject', next.subject);
    } else {
      nextParams.delete('subject');
    }

    if (next.grade && next.grade !== 'all') {
      nextParams.set('grade', next.grade);
    } else {
      nextParams.delete('grade');
    }

    if (next.disableCorrection) {
      nextParams.set('disableCorrection', '1');
    } else {
      nextParams.delete('disableCorrection');
    }

    const nextUrl = nextParams.toString() ? `${navigationPath}?${nextParams.toString()}` : navigationPath;
    const queryChanged = next.query !== currentQuery;
    const filtersChanged = next.subject !== currentSubject || next.grade !== currentGrade;
    const correctionChanged = Boolean(next.disableCorrection) !== currentDisableCorrection;

    if (!queryChanged && !filtersChanged && !correctionChanged) {
      return;
    }

    if (queryChanged) {
      router.push(nextUrl, { scroll: false });
      return;
    }

    router.replace(nextUrl, { scroll: false });
  };

  const handleDiscussWithAdvisor = (item: ParentSearchItem) => {
    if (user) {
      const params = new URLSearchParams();
      params.set('tab', 'search');
      params.set('q', item.title);
      router.push(`/parent?${params.toString()}`);
      return;
    }
    requestAuthPrompt();
  };

  return (
    <main className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 border-b border-[#E5E7EB] bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center justify-between px-4 md:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => router.push('/')}
            className="flex items-center gap-3"
            aria-label="Go to home"
          >
            <AppImage src="/assets/landing-logo.png" alt="Pengi" width={40} height={40} />
            <span className="emphasize-text text-xl font-bold text-[#735648]">Pengi</span>
          </button>
          <div className="flex items-center gap-3">
            {user ? (
              <UserAvatar />
            ) : (
              <button
                type="button"
                onClick={openSignIn}
                className="display-text-semi inline-flex items-center rounded-full border border-[#E5E7EB] bg-white px-4 py-2 text-sm text-[#374151] transition-colors hover:bg-[#F9FAFB]"
              >
                Sign in
              </button>
            )}
          </div>
        </div>
      </header>
      <div className="flex justify-center px-4 py-6 md:px-6 lg:px-8">
        <ParentSearchResultsPanel
          query={query}
          initialData={initialData}
          initialPreviewQuest={initialPreviewQuest}
          initialPreviewToc={initialPreviewToc}
          onQueryChange={setQuery}
          initialSubjectFilter={initialSubject}
          initialGradeFilter={initialGrade}
          initialDisableCorrection={disableCorrectionFromUrl || initialDisableCorrection}
          onSearchStateChange={handleSearchStateChange}
          onDiscussWithAdvisor={handleDiscussWithAdvisor}
          isAuthenticated={Boolean(user)}
          canAssignActions={canAssignActions}
          requireAuthForInternalActions={requireAuthForInternalActions}
          onAuthRequired={(reason) => requestAuthPrompt(reason === 'assign' ? 'assign' : 'complete-question')}
        />
      </div>
      <PublicStudyAuthPromptModal
        visible={authPromptOpen}
        reason={authPromptReason}
        hideContext
        onClose={() => setAuthPromptOpen(false)}
      />
    </main>
  );
}
